import { useLiveQuery } from 'dexie-react-hooks';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { db as defaultDb, type GentleTaskDatabase } from '@/data/db/database';
import { SETTINGS_ID, type Settings } from '@/data/db/settings.schema';
import { ensureSettings, seedWorkspace } from '@/data/db/seed';
import {
  attachLifecycleFlush,
  createSaveCoordinator,
  type SaveCoordinator,
} from '@/data/save-coordinator';
import { createTabSync } from '@/data/tab-sync';
import { requestPersistentStorage } from '@/data/storage';
import {
  attachDesktopCloseFlush,
  autoRestoreIfEmpty,
  createMirrorHealth,
  installMirror,
  repairMirrorIfBehind,
  type MirrorCoordinator,
  type MirrorHealthSnapshot,
} from '@/data/mirror';
import type { SaveState } from '@/features/save-status/save-status';
import { useTheme } from './theme-provider';
import type { ThemeMode } from '@/design/themes/theme';
import { waitForNoteImageOperations } from '@/platform/note-image-files';

interface WorkspaceContextValue {
  db: GentleTaskDatabase;
  settings: Settings | undefined;
  /** settings 尚未讀進來時為 true；此時各頁顯示 skeleton。 */
  isLoading: boolean;
  /** seededAt 有值代表工作台已建立。 */
  isSeeded: boolean;
  seed: () => Promise<void>;
  save: SaveCoordinator;
  saveState: SaveState;
  /** 最後一次成功寫入的 UTC 時間；狀態列要顯示幾點存的。 */
  lastSavedAt: string | null;
  /** SQLite 鏡像。網頁版的每個方法都是 no-op，狀態永遠是 unavailable。 */
  mirror: MirrorCoordinator;
  mirrorHealth: MirrorHealthSnapshot;
}

/** 只問一次持久化儲存；記在 localStorage，清除資料時一併移除。 */
export const PERSISTENCE_ASKED_KEY = 'ui.storage.persistenceAsked';

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({
  children,
  database = defaultDb,
}: {
  children: ReactNode;
  database?: GentleTaskDatabase;
}) {
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [mirrorHealth, setMirrorHealth] = useState<MirrorHealthSnapshot>(() =>
    createMirrorHealth().snapshot(),
  );

  const save = useMemo(
    () =>
      createSaveCoordinator({
        onStateChange: (state, savedAt) => {
          setSaveState(state);
          setLastSavedAt(savedAt);
        },
      }),
    [],
  );

  /**
   * 鏡像必須在任何寫入之前接上去，所以跟 save 一樣用 useMemo 建立一次。
   * 網頁版的 installMirror 會直接短路，不載入任何 Tauri 套件。
   */
  const mirror = useMemo(() => installMirror(database, setMirrorHealth), [database]);
  useEffect(() => {
    mirror.resume();
    return () => mirror.dispose();
  }, [mirror]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      // 空庫 + .db 有資料 = 新電腦或重灌，自動靜默還原（desktop-app.md 第 8 節）。
      // 失敗不擋啟動：鏡像是加分保護，沒有它 App 照樣能用。
      await autoRestoreIfEmpty(database).catch(() => ({ restored: false }));
      await ensureSettings(database);
      await repairMirrorIfBehind(database, mirror).catch(() => {});
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [database, mirror]);

  /**
   * 桌面版按 X 時，Rust 會先擋下關閉並發事件過來。
   * 這裡把還在 debounce 的內容寫出去、把鏡像推完，再回報可以關了。
   */
  useEffect(() => {
    let detach: (() => void) | null = null;
    let cancelled = false;

    void attachDesktopCloseFlush(async () => {
      await waitForNoteImageOperations();
      await save.flush().catch(() => {});
      await mirror.flush().catch(() => {});
    }).then((off) => {
      if (cancelled) off();
      else detach = off;
    });

    return () => {
      cancelled = true;
      detach?.();
    };
  }, [save, mirror]);

  // 通知其他分頁資料已變更（spec 19.2）。Dexie 的 liveQuery 只在本分頁生效。
  useEffect(() => {
    const sync = createTabSync({
      onMessage: () => {
        // 觸發一次 Dexie 的變更事件，讓本分頁的 liveQuery 重新取值。
        void database.settings.get(SETTINGS_ID);
      },
    });
    return () => sync.close();
  }, [database]);

  // 切到背景或關閉分頁時，把還在 debounce 的內容寫出去（spec 6.2）。
  useEffect(() => attachLifecycleFlush(save), [save]);

  useEffect(() => () => save.dispose(), [save]);

  const settings = useLiveQuery(() => database.settings.get(SETTINGS_ID), [database]);

  /**
   * 「首次適當時機」申請持久化儲存（spec 19.1）。
   *
   * 刻意**不在開啟網站時就問**：那會在使用者還沒決定要不要用之前，
   * 就跳出瀏覽器的權限對話框擋住整個畫面。改成建立工作台之後才問一次，
   * 那時使用者已經打算把資料放在這裡了。被拒絕時不阻斷使用。
   */
  useEffect(() => {
    if (!ready) return;
    if (settings?.seededAt == null) return;

    try {
      if (localStorage.getItem(PERSISTENCE_ASKED_KEY)) return;
      localStorage.setItem(PERSISTENCE_ASKED_KEY, '1');
    } catch {
      // 讀不到 localStorage 就每次都問一次，總比完全不問好。
    }
    void requestPersistentStorage();
  }, [ready, settings?.seededAt]);

  const seed = useCallback(async () => {
    await save.run(() => seedWorkspace(database));
  }, [database, save]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      db: database,
      settings,
      isLoading: !ready || settings === undefined,
      isSeeded: settings?.seededAt != null,
      seed,
      save,
      saveState,
      lastSavedAt,
      mirror,
      mirrorHealth,
    }),
    [database, settings, ready, seed, save, saveState, lastSavedAt, mirror, mirrorHealth],
  );

  return (
    <WorkspaceContext value={value}>
      <ThemeSettingsBridge />
      {children}
    </WorkspaceContext>
  );
}

/**
 * settings.theme 是主題的正式歸屬（data-model 8.1）。
 * 載入時以資料庫為準，之後使用者的切換再寫回資料庫。
 */
function ThemeSettingsBridge() {
  const { settings, db } = useWorkspace();
  const { mode, setMode } = useTheme();
  const hydrated = useRef(false);
  /** 上一次同步過的本地值，用來分辨這次不一致是誰造成的。 */
  const syncedMode = useRef<ThemeMode | null>(null);

  useEffect(() => {
    if (!settings) return;
    if (!hydrated.current) {
      hydrated.current = true;
      syncedMode.current = settings.theme;
      if (settings.theme !== mode) setMode(settings.theme);
      return;
    }
    if (settings.theme === mode) {
      syncedMode.current = mode;
      return;
    }
    if (mode !== syncedMode.current) {
      // 本地切換：寫回資料庫。
      syncedMode.current = mode;
      void db.settings.update(SETTINGS_ID, { theme: mode });
    } else {
      // 別的分頁改的：採用它，不要拿本地的值蓋回去造成兩邊來回打架。
      syncedMode.current = settings.theme;
      setMode(settings.theme);
    }
  }, [settings, mode, setMode, db]);

  return null;
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace 必須放在 WorkspaceProvider 之內');
  return context;
}

/** 給 App Shell 這類「有沒有資料層都要能渲染」的元件使用。 */
export function useOptionalWorkspace(): WorkspaceContextValue | null {
  return useContext(WorkspaceContext);
}
